import { getRouteApi } from '@tanstack/react-router';
import { cn } from 'cn';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  SITE_SIGNATURE_RATES_BREAKDOWN_SORT_BY,
  useQueryTopSitesBreakdownWindowed,
} from '~/data/analytics';

import { i18n } from '~/utils';

import { HeadingRow, Icon, Tooltip, withErrorBoundary } from '@vestigas/ui';

import { CHART_COLORS } from '../constants';
import { useDateRange } from '../utils';

import { ChartLegend } from './ChartLegend';
import { DataCard } from './DataCard';
import { InfiniteBarChart } from './InfiniteBarChart';
import { SortOrderSelect } from './SortOrderSelect';

type P = {} & ComponentStyling;

export const SORT_BY_LABELS: Record<
  (typeof SITE_SIGNATURE_RATES_BREAKDOWN_SORT_BY)[number],
  string
> = {
  site_name: i18n.t('usage.signatureRates.sortSiteSignatureRates.siteName', {
    ns: 'analytics',
  }),
  signature_rate: i18n.t(
    'usage.signatureRates.sortSiteSignatureRates.signatureRate',
    {
      ns: 'analytics',
    },
  ),
};

export const CardContent = withErrorBoundary(
  ({ className, style }: P) => {
    const { t } = useTranslation('analytics');

    const AnalyticsUsageRoute = getRouteApi('/(main)/analytics/usage/');
    type SearchParams = ReturnType<typeof AnalyticsUsageRoute.useSearch>;

    const navigate = AnalyticsUsageRoute.useNavigate();

    const { sortBy, sortOrder } = AnalyticsUsageRoute.useSearch({
      select: ({ siteSignatureRatesSortBy, siteSignatureRatesSortOrder }) => ({
        sortBy: siteSignatureRatesSortBy,
        sortOrder: siteSignatureRatesSortOrder,
      }),
    });

    const dateRange = useDateRange();
    const siteSignatureRates = useQueryTopSitesBreakdownWindowed({
      dateRange,
      sortBy,
      sortOrder,
    });

    const [isLoading, setIsLoading] = useState(true);

    async function setSortOrder(
      newSortOrder: SearchParams['siteSignatureRatesSortOrder'],
    ) {
      navigate({
        search: (previous) => ({
          ...previous,
          siteSignatureRatesSortOrder: newSortOrder,
        }),
      });
    }

    async function setSortBy(
      newSortBy: SearchParams['siteSignatureRatesSortBy'],
    ) {
      navigate({
        search: (previous) => ({
          ...previous,
          siteSignatureRatesSortBy: newSortBy,
        }),
      });
    }

    return (
      <>
        <DataCard.Header>
          <HeadingRow
            heading={
              <div className="flex items-center">
                {t(($) => $.usage.signatureRates.topSites.heading)}
                <Tooltip
                  content={t(($) => $.usage.signatureRates.topSites.subHeading)}
                >
                  <span className="inline-block -translate-y-1 pl-1">
                    <Icon
                      className="cursor-help text-tiga-action-default transition-colors duration-300 hover:text-tiga-action-lighter"
                      metaphor="infoOutlined"
                      size="sm"
                    />
                  </span>
                </Tooltip>
              </div>
            }
            action={
              <SortOrderSelect
                className="-mr-1 flex-initial"
                onSortDirectionChange={async () => {
                  await setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
                }}
                onSortFieldChange={async (value) => {
                  if (value === sortBy) {
                    await setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
                  } else {
                    await setSortBy(value);
                  }
                }}
                sortDirection={sortOrder}
                sortField={sortBy}
                labels={SORT_BY_LABELS}
                values={SITE_SIGNATURE_RATES_BREAKDOWN_SORT_BY}
              />
            }
            variant="h3"
          />
        </DataCard.Header>
        <DataCard.Body className="py-0" isLoading={isLoading}>
          <InfiniteBarChart
            className="ml-2"
            colors={CHART_COLORS}
            fetchPage={async (offset, signal) => {
              const result = await siteSignatureRates.fetchPage(offset, signal);

              return {
                data: result.data.map((item) => ({
                  label: item.siteName,
                  tooltipContent: t(
                    ($) => $.usage.signatureRates.topSites.tooltipContent,
                    {
                      signatureRate: item.signatureRate.toFixed(0),
                    },
                  ),
                  value: item.signatureRate,
                })),
                hasNextPage: result.hasNextPage,
                totalCount: result.totalCount,
              };
            }}
            layout="vertical"
            onLoadingChange={setIsLoading}
            pageSize={siteSignatureRates.pageSize}
            queryKey={siteSignatureRates.queryKey}
          />
        </DataCard.Body>
        <DataCard.Footer className="pt-2">
          <ChartLegend
            data={[
              {
                color: CHART_COLORS[0],
                label: t(($) => $.usage.datasetLabel.siteSignatureRates),
              },
            ]}
          />
        </DataCard.Footer>
      </>
    );
  },
  i18n.t(($) => $.errorBoundary.fallbackMessage, {
    ns: 'analytics',
    section: i18n.t(($) => $.usage.signatureRates.topSites.heading, {
      ns: 'analytics',
    }),
  }),
  false,
  'row-span-3',
);

CardContent.displayName = 'SiteSignatureRatesChartContent';

/**
 * SiteSignatureRatesChart
 *
 * Displays a chart of the top sites by signature rate for the selected date range.
 * Uses windowed/sparse data fetching to load only visible data as the user scrolls.
 */
export const SiteSignatureRatesChart = ({ className, style }: P) => {
  return (
    <DataCard
      className={cn('bg-tiga-surface-off-white', className)}
      style={style}
    >
      <CardContent />
    </DataCard>
  );
};
